import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpClient } from '@angular/common/http';
import { ApiEndpoints } from '../../../shared/common/config';
import { User } from '../../../shared/interfaces/user.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { PermissionsList } from '../../../shared/common/PermissionList';
import { UserV2 } from '../interfaces/user';
import { environment } from 'src/environments/environment';
import { SocialUser } from '../interfaces/authentication.interface';
import { externalLogoutApiKeyService, externalStoreApiKeyService } from '@gozetta/dispatch-portal';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  jwtHelper = new JwtHelperService();

  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<any>;

  constructor(private readonly _http: HttpClient) {
    const storedUser = localStorage.getItem('currentUser');
    this.currentUserSubject = new BehaviorSubject<User>(
      storedUser ? JSON.parse(storedUser) : null,
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  /**
   * check if user have access to a spacific page
   *
   */
  checkAccess(pagename: string): boolean {
    let userinfo = JSON.parse(localStorage.getItem('currentUser')!);
    if (
      userinfo?.user_profile.Permissions?.includes(
        PermissionsList[pagename as keyof typeof PermissionsList],
      )
    )
      return true;
    else return false;
  }

  getDispatchApiKey() {
    const currentUser = this.currentUserValue.user_profile;
    const apiKey = JSON.stringify({
      id: currentUser.id,
      email: currentUser.email,
      first_name: currentUser.first_name,
      full_name: currentUser.full_name,
      last_name: currentUser.last_name,
      access_token: currentUser.access_token,
      permissions: [],
      portal_type: 3,
      role: currentUser.role,
      consumer_identity: this.currentUserValue?.identity.organization_id,
    });
    if (!localStorage.getItem('apiKey')) {
      externalStoreApiKeyService(apiKey);
    }
    return apiKey;
  }

  /**
   * check if user LoggedIn
   *
   */
  checkIfLoggedIn() {
    if (!localStorage.getItem('currentUser')) {
      return false;
    }
    let currentUser = JSON.parse(localStorage.getItem('currentUser')!);
    const token = currentUser.user_profile?.access_token;

    if (token && !this.jwtHelper.isTokenExpired(token)) {
      // Token is present and not expired
      return true;
    }
    return false;
  }

  /**
   * login
   *
   */
  login(payload: any): Observable<any> {
    return this._http.post<any>(ApiEndpoints.authentication.login, payload);
  }

  /**
   * save user data
   *
   */
  saveUserData(userinfo: any) {
    localStorage.setItem('consumer_identity', userinfo.identity.organization_id);
    localStorage.setItem('currentUser', JSON.stringify(userinfo));
  }

  /**
   * save user data
   *
   */
  removeUserData() {
    localStorage.setItem('currentUser', '');
    externalLogoutApiKeyService() 
  }

  /**
   * getter: currentUserValue
   *
   */
  public get currentUserValue(): any {
    if (!localStorage.getItem('currentUser')) {
      return false;
    }
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem('currentUser')!),
    );
    this.currentUser = this.currentUserSubject.asObservable();
    return this.currentUserSubject.value;
  }

  public loggedIn() {
    return !!localStorage.getItem('currentUser');
  }
  saveUserDataV2(userinfo: UserV2) {
    localStorage.setItem('currentUser', JSON.stringify(userinfo));
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem('currentUser') ?? '{}'),
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }
  loginBySSO(body: SocialUser): Observable<any> {
    const param = {
      Email: null,
      ClientName: body.clientName,
      IPAddress: '',
      Token: body.token,
      ProviderType: body.providerType,
    };
    return this._http.post<any>(
      `${environment.apiUrl}/provider/authentication/customers-login-sso`,
      param,
    );
  }

  /**
   * timezone
   *
   */
  timeZone() {
    return this._http.get<any>(ApiEndpoints.timeZone.GetTimezone);
  }
}
